<template>
  <DiscardChangesModal
    :open="showDiscardChangesModal"
    :modal_config="props.modal_config"
    @close-modal="showDiscardChangesModal = false"
    @confirm="emit('close-modal')"
  />
  <Modal :open="props.open" :hideClose="true">
    <template #modal-header>
      <div class="">
        <div class="flex items-center">
          <Icon name="Pencil" class="w-4 h-4 mr-1 stroke-info" />
          <div class="text-sm font-inter font-semibold text-info">Editing</div>
        </div>
        <div class="font-league tracking-[-.56px] font-semibold text-[1.75rem] text-strongest">
          Editing {{ props.modal_config.object_to_edit.type }} Trusted Contact
        </div>
      </div>
    </template>

    <template #modal-body>
      <div class="p-6">
        <div>
          <Form
            :schema_array="props.modal_config.schema"
            :state_object="props.modal_config.object_to_edit"
            variant="secondary"
            object_type="trusted_contact"
          />
        </div>
      </div>
    </template>
    <template #modal-footer>
      <div class="flex justify-end gap-2">
        <Button @click="handleConfirmClose()" variant="paper-primary" size="sm" class="">
          <template #label>Cancel</template>
        </Button>
        <Button @click="handleConfirm()" variant="primary-grey" size="sm" class="">
          <template #label>Confirm</template>
        </Button>
      </div>
    </template>
  </Modal>
</template>

<script setup>
// IMPORTS
import { ref, watch } from "vue";
import { Modal, Form, Icon, Button } from "@/components";
import { useCrudStore, useDataStore, useSchemaStore } from "@/stores";
import DiscardChangesModal from "./DiscardChangesModal.vue";

//STORES
const crudStore = useCrudStore();
const dataStore = useDataStore();
const schemaStore = useSchemaStore();

//EMITS AND PROPS
const emit = defineEmits(["close-modal", "confirm"]);
const props = defineProps({
  open: { type: Boolean, required: true },
  modal_config: { type: Object, required: true },
});

const showDiscardChangesModal = ref(false);

//watch props.open to reset show error flag
watch(
  () => props.open,
  (is_open) => {
    if (is_open) {
    } else {
      schemaStore.show_input_errors = false;
    }
  }
);

function handleConfirmClose() {
  let values_changed = checkForChanges();
  let id_info_changed = checkForChanges("id_info");
  if (!values_changed && !id_info_changed) {
    emit("close-modal");
    return;
  }

  // show discard changes modal
  showDiscardChangesModal.value = true;
}

// COMPONENT LOGIC AND FUNCTIONS
async function handleConfirm() {
  try {
    let values_changed = checkForChanges();

    if (!values_changed) {
      emit("close-modal");
      return;
    }

    let _continue = schemaStore.validateFields(
      props.modal_config.object_to_edit,
      props.modal_config.schema,
      null,
      true,
      "trusted_contact",
      props.modal_config.original_object
    );

    if (_continue.proceed) {
      if (props.modal_config.object_to_edit.is_add) {
        await handleNewRecord();
      } else {
        await handleUpdateRecord();
      }

      await handleActivityLog(true, null);

      emit("confirm", {
        type: "trusted_contact",
        record_id: null,
      });

      emit("close-modal");
    } else {
      await handleActivityLog(false, _continue);
    }
  } catch (error) {
    let action_type = props.modal_config.object_to_edit.is_add ? "create" : "update";
    let object_type = "trusted contact";

    var alert = {
      type: "error",
      message: `Failed to ${action_type} ${object_type}.`,
    };
    UIStore.animateNotificationAlert(alert);
  }
}

async function handleNewRecord() {
  let record = props.modal_config.object_to_edit;

  record = schemaStore.addCreatedUpdatedFields(record, true, true);

  record.owner_contact_id = dataStore.contact.record_id;
  record.user_id = dataStore.contact.user_id;
  record.created_date = new Date();
  record.updated_date = new Date();

  delete record.is_add;

  let insert_response = await crudStore.insert("trustedcontacts", [record]);

  if (insert_response.status !== 200) {
    throw new Error("Failed to create trusted contact");
  }
}

async function handleUpdateRecord() {
  let query = [
    {
      field_name: "type",
      operator: "eq",
      value: props.modal_config.object_to_edit.type,
    },
    {
      field_name: "user_id",
      operator: "eq",
      value: dataStore.contact.user_id,
    },
  ];

  let update = props.modal_config.object_to_edit;
  update = schemaStore.addCreatedUpdatedFields(update, false, true);

  let update_response = await crudStore.update("trustedcontacts", query, update);

  if (update_response.status !== 200) {
    throw new Error("Failed to update trusted contact");
  }
}

function checkForChanges() {
  if (props.modal_config.original_object.is_add) {
    return true;
  }

  let original = props.modal_config.original_object;
  let current = props.modal_config.object_to_edit;
  let changed = false;

  for (let key in original) {
    if (original[key] !== current[key]) {
      changed = true;
      break;
    }
  }

  return changed;
}

async function handleActivityLog(valid, validation_details) {
  let status = valid ? "Completed" : "Failed Validation";
  let sub = props.modal_config.object_to_edit.is_add
    ? "Add Trusted Contact"
    : "Edit Trusted Contact";

  let action = props.modal_config.object_to_edit.is_add ? "Adding" : "Editing";

  let log = {
    step: "Profile Page",
    substep: sub,
    status: status,
    message: `${action} ${props.modal_config.object_to_edit.type} Trusted Contact`,
    url: window.location.href,
    validation_details: valid ? null : JSON.stringify(validation_details),
  };

  log = dataStore.setDefaultFields(log, "investoractivity");
  await crudStore.insert("investoractivity", [log]);
}
</script>
